import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';

import type { LauncherApp } from './catalog';

interface AppLinkModalProps {
  /** The app whose links to offer, or `null` when the popup is closed. */
  app: LauncherApp | null;
  onClose: () => void;
}

/** Popup-window features: a separate, sized window rather than a browser tab. */
const NEW_WINDOW_FEATURES = 'noopener,noreferrer,popup,width=1200,height=800';

function safeHost(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/**
 * Small popup shown when an app icon is tapped. It surfaces the app name and
 * lets the user choose how to open it, so they never accidentally navigate the
 * dashboard away. Both actions open in the background so the dashboard stays put.
 */
export default function AppLinkModal({ app, onClose }: AppLinkModalProps) {
  const handleNewTab = () => {
    if (app) window.open(app.url, '_blank', 'noopener,noreferrer');
    onClose();
  };

  const handleNewWindow = () => {
    if (app) window.open(app.url, '_blank', NEW_WINDOW_FEATURES);
    onClose();
  };

  return (
    <Modal
      open={!!app}
      onClose={onClose}
      title={app?.name}
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={handleNewTab}>
            New tab
          </Button>
          <Button type="button" variant="primary" size="sm" onClick={handleNewWindow} autoFocus>
            New window
          </Button>
        </div>
      }
    >
      {app && (
        <div className="flex items-center gap-3">
          <img src={app.icon} alt="" className="h-12 w-12 shrink-0 rounded-lg object-contain" />
          <div className="min-w-0">
            <p className="text-sm text-(--text-secondary)">Open without leaving the dashboard.</p>
            <p className="truncate text-xs text-(--text-tertiary)">{safeHost(app.url)}</p>
          </div>
        </div>
      )}
    </Modal>
  );
}
